import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { CancelSubmitButtons, errorToast } from "~/common/components";
import Modal, { ModalProps } from "~/common/components/Modal";
import { consentDocumentsQueryOptionsFactory } from "~/providers/factories/consentDocumentsQueryOptionsFactory";
import { useCurrentTeamMemberContext } from "~/providers/hooks";
import { useDeleteConsentDocument } from "~/providers/hooks/useDeleteConsentDocument";
import { ListConsentDocumentResponse } from "~/providers/types/consentDocuments";

interface DeleteDocumentModalProps extends ModalProps {
  document: ListConsentDocumentResponse;
}

export const DeleteDocumentModal = ({
  document: { id },
  onClose,
  show,
}: DeleteDocumentModalProps) => {
  const queryClient = useQueryClient();
  const currentTeamMember = useCurrentTeamMemberContext();
  const deleteDocumentMutation = useDeleteConsentDocument();

  const organizationId =
    currentTeamMember.organization_team_members[0].organization_id;

  const onSubmit = () => {
    deleteDocumentMutation.mutate(
      {
        consentId: id,
        organizationId,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: consentDocumentsQueryOptionsFactory.list({
              organizationId,
            }).queryKey,
          });
          onModalClose();
          toast.success("Consent deleted successfully");
        },
        onError: (error) => {
          errorToast(error);
        },
      },
    );
  };

  const onModalClose = () => {
    if (deleteDocumentMutation.isPending) return;
    onClose();
  };

  return (
    <Modal
      title="Delete consent document"
      show={show}
      onClose={onModalClose}
      className="xl:w-1/3"
    >
      <p>Are you sure you want to to delete this consent document?</p>
      <CancelSubmitButtons
        submitButtonLabel="Delete"
        handleSubmit={() => onSubmit()}
        handleCancel={onModalClose}
        isSubmitting={deleteDocumentMutation.isPending}
        containerClassName="w-fit ml-auto"
        disableSubmit={deleteDocumentMutation.isPending}
        disableCancel={deleteDocumentMutation.isPending}
      />
    </Modal>
  );
};
